import { Component } from '@theme/component';

class ResetResearchHub extends Component {
  #controller;
  #runtime;
  #active;
  #loading;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#dispose();
    super.disconnectedCallback();
  }

  #dispose() {
    this.#controller?.abort();
    this.#runtime?.dispose();
    this.#runtime = null;
    this.#loading = null;
    this.#active = null;
  }

  #initialize() {
    this.#dispose();
    const controller = new AbortController();
    this.#controller = controller;
    const { signal } = controller;
    this.addEventListener('click', event => {
      const control = event.target.closest?.('[data-peptide]');
      if (!control || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopPropagation();
      location.hash = '#compound/' + encodeURIComponent(control.dataset.peptide);
    }, { capture: true, signal });
    window.addEventListener('hashchange', () => this.#route(), { signal });
    const legacy = new URL(location.href).searchParams.get('peptide');
    if (legacy && (!location.hash || location.hash === '#map')) {
      const url = new URL(location.href);
      url.searchParams.delete('peptide');
      url.hash = '#compound/' + encodeURIComponent(legacy);
      history.replaceState(history.state, '', url);
    }
    this.#route();
  }

  async #loadResearch() {
    if (this.#runtime) return this.#runtime;
    if (this.#loading) return this.#loading;
    const signal = this.#controller.signal;
    const mount = this.querySelector('[data-research-mount]');
    const root = mount.shadowRoot || mount.attachShadow({ mode: 'open' });
    root.innerHTML = '';
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href = this.dataset.styles;
    const surface = document.createElement('div');
    surface.className = 'research-surface';
    surface.innerHTML = '<div id="app"><p class="loading" role="status">Opening the encyclopedia…</p></div><div id="notice" role="status" aria-live="polite"></div>';
    root.append(style, surface);
    this.#loading = import(this.dataset.runtime).then(module => module.mountResearch(root, { dataUrl: this.dataset.research, signal })).then(runtime => {
      if (signal.aborted) { runtime?.dispose(); return null; }
      this.#runtime = runtime;
      return runtime;
    }).catch(error => {
      if (signal.aborted) return null;
      const message = document.createElement('p');
      message.className = 'loading';
      message.textContent = 'The encyclopedia could not load. Reload the page to try again.';
      root.getElementById('app').replaceChildren(message);
      this.#loading = null;
      console.error('Reset research library:', error);
      return null;
    });
    return this.#loading;
  }

  async #route() {
    const signal = this.#controller.signal;
    const hash = location.hash || '#' + this.dataset.defaultView;
    const view = hash === '#map' ? 'map' : hash === '#calculator' ? 'calculator' : hash === '#sources' ? 'sources' : 'library';
    this.querySelector('[data-map-panel]').hidden = view !== 'map';
    this.querySelector('[data-research-panel]').hidden = view === 'map';
    for (const link of this.querySelectorAll('[data-hub-tab]')) {
      if (link.dataset.hubTab === view) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
    if (view === 'map') window.dispatchEvent(new Event('resize'));
    else {
      const runtime = await this.#loadResearch();
      if (!signal.aborted && runtime) runtime.route();
    }
    if (signal.aborted) return;
    if (this.#active && this.#active !== hash) window.scrollTo({ top: 0, behavior: 'instant' });
    this.#active = hash;
  }
}

if (!customElements.get('reset-research-hub')) customElements.define('reset-research-hub', ResetResearchHub);
